import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons/faBadgeCheck';
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons/faCalendarClock';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { faMoneyCheckDollarPen } from '@fortawesome/pro-regular-svg-icons/faMoneyCheckDollarPen';
import { faUserClock } from '@fortawesome/pro-regular-svg-icons/faUserClock';
import { faUniversity } from '@fortawesome/pro-solid-svg-icons/faUniversity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Grid, Link, Stack, Typography } from '@mui/material';
import { MprcButton, MprcCard, MprcContainer, MprcText, MprcTypography } from 'components';
import { MprcFeedTypography } from 'components/MprcFeedTypography';
import { EnrollmentWorkflowStatus, EnrollStatus } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import moment from 'moment';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LmsTrainingDefaultThumbnailUrl } from 'views/lms/components/LmsTypeImage';
import { useEnrollmentViewContext } from 'views/lms/student/EnrollmentView';
import { ConclusionFormDialog } from 'views/lms/student/EnrollmentView/ECourse/ConclusionFormDialog';
import { getEnrollmentCertificate } from 'views/lms/student/EnrollmentView/Shared/EnrollmentActions';
import { TrainingAvatar } from 'views/lms/student/EnrollmentView/Shared/TrainingAvatar';
import { TrainingDueDate } from 'views/lms/student/EnrollmentView/Shared/TrainingDueDate';
import { TrainingInteractions } from 'views/lms/student/EnrollmentView/Shared/TrainingInteractions';

/////////////////////////////////////////////////////////////////////////////////////

export const ECourseEnrollmentViewHeader: React.FC = () => {

    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    const { enrollment, onReload } = useEnrollmentViewContext();

    const [dialog, setDialog] = useState<any>();

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderActions = useMemo(() => {
        if (enrollment.finalizationDate == null) {
            return <Grid sx={{ width: 240 }}>
                <MprcButton
                    fullWidth
                    key="principalAction"
                    variant="contained"
                    text={enrollment.eCourse?.lastSubmit != null ? t("Student.EnrollmentView.ECourse.EditFormButtonText") : t("Student.EnrollmentView.ECourse.FillFormButtonText")}
                    title={enrollment.eCourse?.lastSubmit != null ? t("Student.EnrollmentView.ECourse.EditFormButtonText") : t("Student.EnrollmentView.ECourse.FillFormButtonText")}
                    onClick={() => {
                        setDialog(
                            <ConclusionFormDialog
                                trainingId={enrollment.trainingId}
                                classroomId={enrollment.classroomId}
                                readonly={enrollment.readOnly}
                                enrollmentId={enrollment.isPreview ? null : enrollment.enrollmentId}
                                onClose={(result) => { setDialog(null); result && onReload(); }}
                            />
                        );
                    }}
                />
            </Grid>;
        }
    }, [enrollment, t]);

    ///
    const RenderTitle = useMemo(() => {
        return (
            <Grid container justifyContent="space-between" alignItems="center" direction="row" wrap="nowrap">
                <Grid size="grow">
                    <MprcFeedTypography variant="title" color="black" component="div" noWrap>{enrollment.trainingName}</MprcFeedTypography>
                    {enrollment.multipleClasses && <Typography variant="caption" color="muted" component="p" noWrap>{enrollment.classroomName}</Typography>}
                </Grid>
            </Grid>
        );
    }, [enrollment, t]);

    ///
    const RenderTitleCompleted = useMemo(() => {
        return (
            <Grid container alignItems="flex-start" direction="row" wrap="nowrap" spacing={2}>
                <Grid>
                    <Avatar
                        variant="rounded"
                        sx={{ width: 111, height: 70 }}
                        src={LmsTrainingDefaultThumbnailUrl(enrollment.trainingContentType, enrollment.trainingThumbnailUrl)}
                    />
                </Grid>
                <Grid size="grow">
                    <MprcTypography variant="h3" textTransform="unset" color="muted" component="div" noWrapRows={2} fontSize={18}>{enrollment.trainingName}</MprcTypography>
                    {enrollment.multipleClasses && <Typography variant="caption" color="muted" component="p" noWrap sx={{ mt: 1 }}>{enrollment.classroomName}</Typography>}
                </Grid>
            </Grid>
        );
    }, [enrollment, t]);

    ///
    const RenderContent = useMemo(() => {

        return (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2} wrap="nowrap">
                        <Grid>
                            <TrainingAvatar />
                        </Grid>
                        <Grid size="grow">
                            <MprcContainer>
                                {(enrollment.status == EnrollStatus.NotStarted || enrollment.status == EnrollStatus.InProgress) &&
                                    <TrainingDueDate enrollment={enrollment} />
                                }
                            </MprcContainer>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                        {RenderActions}
                        <Grid size="grow">
                            <TrainingInteractions enrollment={enrollment} readOnly={enrollment.isPreview} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }, [enrollment, t]);

    ///
    const RenderContentCompleted = useMemo(() => {

        return (
            <>
                <Grid container spacing={2}>
                    {enrollment.eCourse?.showInstitutionOrProvider === true &&
                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FontAwesomeIcon icon={faUniversity} fontSize={30}></FontAwesomeIcon>
                                <Typography variant="h6">
                                    {enrollment.eCourse?.institutionOrProvider ? enrollment.eCourse?.institutionOrProvider : "N/D"}
                                </Typography>
                            </Stack>
                        </Grid>
                    }
                    {enrollment.eCourse?.showCoursePeriod === true &&
                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FontAwesomeIcon icon={faCalendarClock} fontSize={30}></FontAwesomeIcon>
                                <Stack>
                                    <Typography variant="h6">
                                        {t("Student.EnrollmentView.ECourse.StartDateText")}&nbsp;
                                        {enrollment.eCourse?.startDate ? moment(enrollment.eCourse?.startDate).format("L") : "N/D"}
                                    </Typography>
                                    <Typography variant="h6" color={enrollment.eCourse?.endDate ? "success.main" : "muted.dark"} sx={enrollment.eCourse?.endDate ? { fontWeight: "bold" } : {}}>
                                        {t("Student.EnrollmentView.ECourse.EndDateText")}&nbsp;
                                        {enrollment.eCourse?.endDate ? moment(enrollment.eCourse?.endDate).format("L") : "N/D"}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Grid>
                    }
                    {enrollment.eCourse?.showPricePaid === true &&
                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FontAwesomeIcon icon={faMoneyCheckDollarPen} fontSize={30}></FontAwesomeIcon>
                                <Typography variant="h6">
                                    {enrollment.eCourse?.pricePaid ?
                                        Formaters.currency(enrollment.eCourse?.pricePaid ?? 0)
                                        : "N/D"
                                    }
                                </Typography>
                            </Stack>
                        </Grid>
                    }
                    {(enrollment.eCourse?.showCourseDuration === true || enrollment.eCourse?.showAttendancePercent === true) &&
                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FontAwesomeIcon icon={faUserClock} fontSize={30}></FontAwesomeIcon>
                                <Stack>
                                    {enrollment.eCourse?.showCourseDuration === true &&
                                        <Typography variant="h6">
                                            {t("Student.EnrollmentView.ECourse.DurationText")}&nbsp;
                                            {enrollment.eCourse?.duration ? Formaters.duration(enrollment.eCourse?.duration ?? 0) : "N/D"}
                                        </Typography>
                                    }
                                    {enrollment.eCourse?.showAttendancePercent === true &&
                                        <Typography variant="h6">
                                            {t("Student.EnrollmentView.ECourse.AttendanceText")}&nbsp;
                                            {enrollment.eCourse?.attendancePercent ? `${enrollment.eCourse?.attendancePercent ?? 0}%` : "N/D"}
                                        </Typography>
                                    }
                                </Stack>
                            </Stack>
                        </Grid>
                    }
                    {(enrollment.eCourse?.showFinalScore === true || enrollment.status == EnrollStatus.Approved || enrollment.status == EnrollStatus.Reproved || enrollment.status == EnrollStatus.ConclusionFormRejected) &&
                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FontAwesomeIcon icon={faBadgeCheck} fontSize={30}></FontAwesomeIcon>
                                <div>
                                    <Typography variant="h6">
                                        <Stack>
                                            {enrollment.eCourse?.showFinalScore === true &&
                                                <span>
                                                    {t("Student.EnrollmentView.ECourse.FinalScoreText")}&nbsp;
                                                    {enrollment.eCourse.finalScore ? `${enrollment.eCourse.finalScore ?? 0}${enrollment.eCourse.finalScoreIsPercent === true ? "%" : ""}` : "N/D"}
                                                </span>
                                            }
                                            {(enrollment.status == EnrollStatus.Approved || enrollment.status == EnrollStatus.Reproved || enrollment.status == EnrollStatus.ConclusionFormRejected) &&

                                                <Typography
                                                    variant="inherit"
                                                    sx={{ textTransform: "uppercase" }}
                                                    color={enrollment.status == EnrollStatus.Approved ? "success.main" : "error.main"}
                                                >
                                                    <b>
                                                        {
                                                            enrollment.status == EnrollStatus.Approved
                                                                ? t("Student.EnrollmentView.ApprovedText")
                                                                : enrollment.status == EnrollStatus.Reproved
                                                                    ? t("Student.EnrollmentView.ReprovedText")
                                                                    : t("Student.EnrollmentView.RejectedText")
                                                        }
                                                    </b>
                                                </Typography>
                                            }
                                        </Stack>
                                    </Typography>
                                </div>
                            </Stack>
                        </Grid>
                    }
                    {enrollment.eCourse?.showCertificate === true &&
                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <FontAwesomeIcon icon={faMoneyCheckDollarPen} fontSize={30}></FontAwesomeIcon>
                                <Stack>
                                    {enrollment.eCourse?.externalCertificateId == null
                                        ? "N/D"
                                        : <Link variant="h6"
                                            color={"primary.light"}
                                            onClick={() => getEnrollmentCertificate(enrollment.eCourse!.externalCertificateId!)}
                                        >
                                            &nbsp;{t("Student.EnrollmentView.ECourse.ViewCertificateText")}
                                        </Link>
                                    }
                                    {enrollment.certificateValidUntil != null &&
                                        <Typography variant="h6" color={enrollment.certificateExpired ? "error.main" : "muted.dark"}>
                                            {enrollment.certificateExpired
                                                ? <>{t("Student.EnrollmentView.ECourse.CertificateExpiredText")}&nbsp;{moment(enrollment.certificateValidUntil!).format("L")}</>
                                                : <>{t("Student.EnrollmentView.ECourse.CertificateValidUntilText")}&nbsp;{moment(enrollment.certificateValidUntil!).format("L")}</>
                                            }
                                        </Typography>
                                    }
                                </Stack>
                            </Stack>
                        </Grid>
                    }
                    {enrollment.eCourse?.approver != null
                        ? (
                            <Grid size={{ xs: 12 }}>
                                <MprcCard paperColor="notice">
                                    <Box sx={{ p: 1 }}>
                                        <MprcContainer margin="dense">
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid>
                                                    <Link onClick={() => showUser(enrollment.eCourse!.approver!.userId)}>
                                                        <Avatar
                                                            sx={{ width: 35, height: 35 }}
                                                            src={enrollment.eCourse.approver.thumbnailUrl}
                                                        />
                                                    </Link>
                                                </Grid>
                                                <Grid size="grow">
                                                    <Stack>
                                                        {(enrollment.eCourse.approvalWorkflowStatus == EnrollmentWorkflowStatus.NotStarted ||
                                                            enrollment.eCourse.approvalWorkflowStatus == EnrollmentWorkflowStatus.Pending)
                                                            ?
                                                            <>
                                                                <Typography variant="h6" color="muted.main">
                                                                    {t("Student.EnrollmentView.ECourse.ApprovalSubmittedText")}
                                                                </Typography>
                                                                <Typography variant="h6" color="muted.main">
                                                                    {t("Student.EnrollmentView.ECourse.WaitingApprovalByText")} <MprcText color={["primary", "light"]}>{enrollment.eCourse.approver.name}</MprcText>
                                                                </Typography>
                                                            </>
                                                            : <>
                                                                {enrollment.eCourse.approvalWorkflowStatus == EnrollmentWorkflowStatus.Approved &&
                                                                    <Typography variant="h6" color="success.main">
                                                                        {t("Student.EnrollmentView.ECourse.ApprovedByText")}
                                                                    </Typography>
                                                                }
                                                                {enrollment.eCourse.approvalWorkflowStatus == EnrollmentWorkflowStatus.Rejected &&
                                                                    <Typography variant="h6" color="error.main" textTransform="uppercase">
                                                                        {t("Student.EnrollmentView.ECourse.RejectedText")}
                                                                    </Typography>
                                                                }
                                                                <Link variant="h6" onClick={() => showUser(enrollment.eCourse!.approver!.userId)}>
                                                                    {enrollment.eCourse.approver.name}
                                                                </Link>
                                                            </>
                                                        }
                                                    </Stack>
                                                </Grid>
                                                <Grid>
                                                    {enrollment.eCourse.approvalWorkflowStatus == EnrollmentWorkflowStatus.Approved &&
                                                        <MprcText color="success">
                                                            <FontAwesomeIcon icon={faCircleCheck} fontSize={35} />
                                                        </MprcText>
                                                    }
                                                    {enrollment.eCourse.approvalWorkflowStatus == EnrollmentWorkflowStatus.Rejected &&
                                                        <MprcText color="error">
                                                            <FontAwesomeIcon icon={faCircleXmark} fontSize={35} />
                                                        </MprcText>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </MprcContainer>
                                        <Typography variant="caption">
                                            {enrollment.eCourse.approverMessage}
                                        </Typography>
                                    </Box>
                                </MprcCard>
                            </Grid>
                        )
                        : enrollment.eCourse?.needWorkflow === false &&
                        (
                            <Grid size={{ xs: 12 }}>
                                <MprcCard paperColor="notice">
                                    <Box sx={{ p: 1 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid size="grow">
                                                <Typography variant="h6" color="muted.main">
                                                    {t("Student.EnrollmentView.ECourse.AutommaticApprovalText")}
                                                    &nbsp;
                                                    {Formaters.performaDate(enrollment.eCourse?.lastSubmit)}
                                                </Typography>
                                            </Grid>
                                            <Grid>
                                                <MprcText color="success">
                                                    <FontAwesomeIcon icon={faCircleCheck} fontSize={35} />
                                                </MprcText>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </MprcCard>
                            </Grid>
                        )
                    }
                    <Grid size={{ xs: 12 }}>
                        <Grid container spacing={2}>
                            {RenderActions}
                            <Grid size="grow">
                                <TrainingInteractions enrollment={enrollment} readOnly={enrollment.isPreview} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </>
        );
    }, [enrollment, t]);


    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////
    return (
        <>
            {dialog}
            <MprcContainer>
                <MprcCard>
                    <Box sx={{ p: 1.5 }}>
                        <Grid container direction="row" spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                {enrollment.eCourse?.lastSubmit != null ? RenderTitleCompleted : RenderTitle}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                {enrollment.eCourse?.lastSubmit != null ? RenderContentCompleted : RenderContent}
                            </Grid>
                        </Grid>
                    </Box>
                </MprcCard>
            </MprcContainer>
        </>
    );
};
